import React, { FC, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ApolloProvider } from '@apollo/react-hooks';
import { Amplify, Auth } from 'aws-amplify';
import CssBaseline from '@material-ui/core/CssBaseline';
import { ThemeProvider, Theme, makeStyles, createStyles } from '@material-ui/core/styles';
import { AlertProvider } from '../context/alertContext';
import { LicenseEndpointProvider } from '../context/licenseEndpointContext';
import { useLanguageContext } from './languages';
import Routes from './Routes';
import Header from './common/Header';
import Sidebar from './common/Sidebar';
import Alert from './common/Alert';
import client from '../utils/client';
import awsConfig from '../aws-exports';
import { getTheme } from '../theme';
import { QueryClient, QueryClientProvider } from 'react-query';

Auth.configure(awsConfig);
Amplify.configure({
	API: {
		endpoints: [
			{
				name: 'sports-data',
				endpoint: awsConfig.endpoint_url,
				region: awsConfig.region
			}
		]
	}
});

const FRAMELESS_ROUTES = ['/report'];

const sidebarWidth = 200;

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			display: 'flex',
			maxWidth: '100%'
		},
		content: {
			flexGrow: 1,
			backgroundColor: theme.palette.background.default,
			padding: theme.spacing(3),
			maxWidth: '100%'
		},
		contentHeaderPadding: {
			...theme.mixins.toolbar
		}
	})
);

const App: FC = () => {
	const classes = useStyles();
	const { language } = useLanguageContext();
	const theme = getTheme(language.key);

	const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
	const toggleMobileSidebar = (): void => setMobileSidebarOpen(!mobileSidebarOpen);

	const isFrameless = FRAMELESS_ROUTES.includes(useLocation().pathname);

	const queryClient = new QueryClient({
		defaultOptions: {
			queries: {
				refetchOnWindowFocus: false
			}
		}
	});

	return (
		<ApolloProvider client={client}>
			<QueryClientProvider client={queryClient}>
				<ThemeProvider theme={theme}>
					<LicenseEndpointProvider>
						<AlertProvider>
							<CssBaseline />
							<div className={classes.root}>
								{!isFrameless && (
									<>
										<Alert />
										<Header environment={awsConfig.stage} sidebarWidth={sidebarWidth} onOpenMobileSidebar={toggleMobileSidebar} />
										<Sidebar width={sidebarWidth} mobileOpen={mobileSidebarOpen} onCloseMobileSidebar={toggleMobileSidebar} />
									</>
								)}
								<main data-testid='app-main' className={classes.content}>
									{!isFrameless && <div className={classes.contentHeaderPadding} />}
									<Routes />
								</main>
							</div>
						</AlertProvider>
					</LicenseEndpointProvider>
				</ThemeProvider>
			</QueryClientProvider>
		</ApolloProvider>
	);
};

export default App;
